/**
 * Path/polygon selection hit-testing (Faz 7.8 — OCS select.rs/fence.rs +
 * scene path_hit parity, world-space decision documented in the phase file:
 * yap keeps the even-odd tests in world space instead of OCS's screen-space
 * variant — same semantics, no NaN sub-path exposure).
 *
 * - Fence: an open wire path selects every entity whose wire segments cross
 *   a fence leg (point markers included via a proximity test). Area-only
 *   geometry (hatch fills) does NOT match — the OCS fence rule.
 * - WPolygon (window): every wire point must lie inside the polygon
 *   (OCS all_inside + disqualified set).
 * - CPolygon (crossing): any point inside OR any wire segment crossing the
 *   polygon boundary selects the entity.
 */

import { useAppStore, type Entity } from '../../../state/store';
import type { Pt3, Segment2 } from '../geometryMath';
import { pointInPolygon2 } from '../geometryMath';

/** Segments intersect (closed ab / cd segments, XY). */
export function segmentsIntersect(a: Pt3, b: Pt3, c: Pt3, d: Pt3): boolean {
    const r = { x: b[0] - a[0], y: b[1] - a[1] };
    const s = { x: d[0] - c[0], y: d[1] - c[1] };
    const denom = r.x * s.y - r.y * s.x;
    if (Math.abs(denom) < 1e-12) return false;
    const t = ((c[0] - a[0]) * s.y - (c[1] - a[1]) * s.x) / denom;
    const u = ((c[0] - a[0]) * r.y - (c[1] - a[1]) * r.x) / denom;
    const tol = 1e-9;
    return t >= -tol && t <= 1 + tol && u >= -tol && u <= 1 + tol;
}

/** Wire segments of an entity (analytic rims for circles/arcs/ellipses). */
export function wireSegmentsOf(e: Entity): Segment2[] {
    const d = e.data as Record<string, any>;
    const segs: Segment2[] = [];
    const chain = (pts: Pt3[], closed: boolean) => {
        const n = pts.length;
        if (n < 2) return;
        const count = closed ? n : n - 1;
        for (let i = 0; i < count; i++) segs.push({ a: pts[i], b: pts[(i + 1) % n] });
    };
    const rim = (center: number[], r: number, from: number, to: number, steps: number) => {
        let sweep = to - from;
        while (sweep <= 0) sweep += Math.PI * 2;
        const pts: Pt3[] = [];
        for (let i = 0; i <= steps; i++) {
            const a = from + (sweep * i) / steps;
            pts.push([center[0] + Math.cos(a) * r, center[1] + Math.sin(a) * r, center[2] ?? 0]);
        }
        chain(pts, false);
    };
    switch (e.type) {
        case 'line':
            if (Array.isArray(d.start) && Array.isArray(d.end)) segs.push({ a: d.start as Pt3, b: d.end as Pt3 });
            break;
        case 'polyline':
        case 'polygon':
            chain((d.vertices ?? []) as Pt3[], !!d.closed || e.type === 'polygon');
            break;
        case 'rectangle': {
            const [x1, y1] = d.corner1 ?? [0, 0];
            const [x2, y2] = d.corner2 ?? [0, 0];
            const z = d.corner1?.[2] ?? 0;
            chain([[x1, y1, z], [x2, y1, z], [x2, y2, z], [x1, y2, z]], true);
            break;
        }
        case 'circle':
            rim(d.center ?? [0, 0], d.radius ?? 0, 0, Math.PI * 2, 32);
            break;
        case 'arc':
            rim(d.center ?? [0, 0], d.radius ?? 0, d.startAngle ?? 0, d.endAngle ?? 0, 16);
            break;
        case 'ellipse': {
            const rot = d.rotation ?? 0;
            const pts: Pt3[] = [];
            const seg = 48;
            for (let i = 0; i <= seg; i++) {
                const t = (i / seg) * Math.PI * 2;
                const x = (d.radiusA ?? 1) * Math.cos(t), y = (d.radiusB ?? 1) * Math.sin(t);
                pts.push([
                    d.center[0] + x * Math.cos(rot) - y * Math.sin(rot),
                    d.center[1] + x * Math.sin(rot) + y * Math.cos(rot),
                    d.center[2] ?? 0,
                ]);
            }
            chain(pts, false);
            break;
        }
        case 'curve':
        case 'spline':
        case 'nurbs-curve':
            chain((d.tessellation ?? d.controlPoints ?? []) as Pt3[], !!d.closed);
            break;
        case 'leader':
            if (Array.isArray(d.tip) && Array.isArray(d.landing)) segs.push({ a: d.tip as Pt3, b: d.landing as Pt3 });
            break;
    }
    return segs;
}

/** Sampled wire points of an entity (polygon membership tests). */
export function wirePointsOf(e: Entity): Pt3[] {
    if (e.type === 'point') {
        const p = e.data?.position;
        return Array.isArray(p) ? [p as Pt3] : [];
    }
    const segs = wireSegmentsOf(e);
    if (segs.length === 0) return [];
    const pts: Pt3[] = [];
    segs.forEach((s) => {
        if (!pts.some((q) => Math.hypot(q[0] - s.a[0], q[1] - s.a[1]) < 1e-9)) pts.push(s.a);
        pts.push(s.b);
    });
    return pts;
}

/** Visible + unlocked candidates (locked layers respected). */
export function selectableEntities(): Entity[] {
    const { entities, layers } = useAppStore.getState();
    const lockedLayers = new Set(
        Object.values(layers).filter((l) => l.locked).map((l) => l.name)
    );
    return Object.values(entities).filter((e) => e.visible && !e.locked && !lockedLayers.has(e.layer));
}

/** FENCE: wire crossing with the legs (hatch excluded — OCS rule). */
export function entitiesOnFence(legs: Pt3[]): string[] {
    const ids: string[] = [];
    const fenceSegs: Segment2[] = [];
    for (let i = 0; i < legs.length - 1; i++) fenceSegs.push({ a: legs[i], b: legs[i + 1] });
    selectableEntities().forEach((e) => {
        if (e.type === 'hatch') return; // area geometry never matches the fence
        if (e.type === 'point') {
            // Point markers: a fence leg passing within a small radius.
            const p = e.data?.position as Pt3 | undefined;
            if (!p) return;
            const hit = fenceSegs.some((s) => {
                const dx = s.b[0] - s.a[0], dy = s.b[1] - s.a[1];
                const len2 = dx * dx + dy * dy || 1;
                const t = Math.max(0, Math.min(1, ((p[0] - s.a[0]) * dx + (p[1] - s.a[1]) * dy) / len2));
                const fx = s.a[0] + dx * t, fy = s.a[1] + dy * t;
                return Math.hypot(p[0] - fx, p[1] - fy) < 1e-4;
            });
            if (hit) ids.push(e.id);
            return;
        }
        const segs = wireSegmentsOf(e);
        const hit = segs.some((s) => fenceSegs.some((f) => segmentsIntersect(s.a, s.b, f.a, f.b)));
        if (hit) ids.push(e.id);
    });
    return ids;
}

/** WPOLYGON: every wire point strictly inside the closed polygon. */
export function entitiesInWindowPolygon(polygon: Pt3[]): string[] {
    const ids: string[] = [];
    selectableEntities().forEach((e) => {
        const pts = wirePointsOf(e);
        if (pts.length === 0) return;
        if (pts.every((p) => pointInPolygon2(p, polygon))) ids.push(e.id);
    });
    return ids;
}

/** CPOLYGON: any point inside OR any wire segment crossing the boundary. */
export function entitiesInCrossingPolygon(polygon: Pt3[]): string[] {
    const ids: string[] = [];
    const boundary: Segment2[] = [];
    for (let i = 0; i < polygon.length; i++) {
        boundary.push({ a: polygon[i], b: polygon[(i + 1) % polygon.length] });
    }
    selectableEntities().forEach((e) => {
        const pts = wirePointsOf(e);
        if (pts.length === 0) return;
        if (pts.some((p) => pointInPolygon2(p, polygon))) { ids.push(e.id); return; }
        const segs = wireSegmentsOf(e);
        if (segs.some((s) => boundary.some((b) => segmentsIntersect(s.a, s.b, b.a, b.b)))) ids.push(e.id);
    });
    return ids;
}
